import { useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Box,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { BOXICON_OPTIONS, FIELD_TYPES, ICON_OPTIONS, NODE_TYPES } from "common/constants/enums";
import { useAppDispatch, useAppSelector } from "common/hooks";
import { addNode, deleteNode, setNodeToAdd, setNodeToEdit, updateNode } from "store/slices/nodes";
import { handleNodeState } from "common/constants/functions";
import CreateConditions from "./CreateConditions";
import _ from "lodash";
import Substitutions from "common/components/Substitutions";
import Links from "common/components/Links";
import Values from "common/components/Values";
import Validations from "./Validations";
import DrawTwoToneIcon from "@mui/icons-material/DrawTwoTone";
import { toast } from "react-toastify";
import { DisplayIcon } from "./DisplayIcon";
import ConfirmActionPopup from "./ConfirmActionPopup";
import { Delete } from "@mui/icons-material";

export default function NodeForm() {
  const {
    json: { nodes },
    nodeToEdit,
    nodeToAdd,
  } = useAppSelector((state) => state.nodes);
  const fixedValueTypes = [
    FIELD_TYPES.boolean,
    FIELD_TYPES.dropdown,
    FIELD_TYPES.singleselect,
    FIELD_TYPES.multiselect,
  ];
  const dispatch = useAppDispatch();
  const [
    { isQuestionNode, isSectionNode, isQuestionGroupNode, isSectionSummeryGroupNode },
    setNodeType,
  ] = useState({
    isQuestionNode: false,
    isSectionNode: false,
    isQuestionGroupNode: false,
    isSectionSummeryGroupNode: false,
    isSubQuestionGroupNode: false,
  });

  useEffect(() => {
    const nodeType = nodeToEdit?.data?.nodeType || nodeToAdd?.nodeType;
    setNodeType({
      isQuestionNode: nodeType === NODE_TYPES.question,
      isSectionNode: nodeType === NODE_TYPES.section,
      isQuestionGroupNode: nodeType === NODE_TYPES.questionGroup,
      isSectionSummeryGroupNode: nodeType === NODE_TYPES.sectionsummarygroup,
      isSubQuestionGroupNode: nodeType === NODE_TYPES.subquestiongroup,
    });
  }, [nodeToEdit?.data?.nodeType, nodeToAdd?.nodeType]);
  const INITIAL_FORM_STATE = useMemo(() => {
    const initialState = isQuestionNode
      ? {
          id: "",
          data: {
            nodeTitle: "",
            nodeType: NODE_TYPES.question,
            properties: {
              type: FIELD_TYPES?.textbox,
              properties: {
                placeholder: "",
                values: [],
              },
              title: {
                condition: true,
                ifValue: "",
                elseValue: "",
                substitutions: {},
                links: {},
              },
              defaultValue: {
                condition: false,
                ifValue: "",
                elseValue: "",
                substitutions: {},
              },
              editable: { condition: true, ifValue: true, elseValue: true },
              shouldContinue: true,
            },
            style: { direction: "", row: "" },
            required: {
              condition: true,
              ifValue: true,
              elseValue: false,
            },
            visibility: {
              condition: true,
              ifValue: true,
              elseValue: false,
            },
          },
          parentNode: nodeToAdd?.parentNode,
        }
      : {
          id: "",
          data: {
            nodeTitle: "",
            nodeType: nodeToAdd?.nodeType,
            icon: "",
            boxIcon: "",
            style: { justify: "" },
            properties: {
              title: {
                condition: true,
                ifValue: "",
                elseValue: "",
                substitutions: {},
                links: {},
              },
            },
            visibility: {
              condition: true,
              ifValue: true,
              elseValue: false,
            },
          },
          parentNode: nodeToAdd?.parentNode,
        };
    return initialState;
  }, [isQuestionNode, nodeToAdd]);

  const [open, setOpen] = useState(false);
  const [nodeState, setNodeState] = useState<any>(INITIAL_FORM_STATE);

  const [isDuplicateId, setIsDuplicateId] = useState(false);
  const [title, setTitle] = useState("");
  const [isEqual, setIsEqual] = useState(true);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);

  useEffect(() => {
    nodeToAdd &&
      (nodes?.some((n: any) => n?.id === nodeToAdd?.parentNode) ||
        nodeToAdd?.nodeType === NODE_TYPES.section ||
        dispatch(setNodeToAdd(null)));
    nodeToEdit &&
      (nodes?.some((n: any) => n?.id === nodeToEdit?.id) || dispatch(setNodeToEdit(null)));
    // eslint-disable-next-line
  }, [nodes]);

  useEffect(() => {
    let currentTitle = "";
    switch (nodeToEdit?.data?.nodeType || nodeToAdd?.nodeType) {
      case NODE_TYPES.section:
        currentTitle = "Section";
        break;
      case NODE_TYPES.sectionsummarygroup:
        currentTitle = "Section Summary Group";
        break;
      case NODE_TYPES.questionGroup:
        currentTitle = "Question Group";
        break;
      case NODE_TYPES.subquestiongroup:
        currentTitle = "Sub Question Group";
        break;
      case NODE_TYPES.question:
        currentTitle = "Question";
        break;

      default:
        currentTitle = "Node";
        break;
    }
    setTitle(currentTitle);
    setIsDuplicateId(false);
  }, [nodeToAdd, nodeToEdit]);

  useEffect(() => {
    if (nodeToAdd) {
      const parentId = nodes?.find((n: any) => n?.id === nodeToAdd?.parentNode)?.id ?? "";
      const nweChildId =
        nodes?.filter(
          (n: any) =>
            n?.data?.nodeType === nodeToAdd?.nodeType && n?.parentNode === nodeToAdd?.parentNode
        ).length + 1;
      setOpen(true);
      dispatch(setNodeToEdit(null));
      setNodeState(INITIAL_FORM_STATE);
      const uniqueId: any = (id: string) => {
        const existingIds = nodes?.map((n: any) => n?.id) || [];
        if (existingIds.includes(id)) {
          const match = id.match(/(.+?)(\d+)$/);
          if (match) {
            const prefix = match[1];
            const number = parseInt(match[2], 10);
            console.log(prefix, number);
            return uniqueId(`${prefix}${number + 1}`);
          }
        }
        return id;
      };
      switch (nodeToAdd?.nodeType) {
        case NODE_TYPES.section:
          handleNodeState(["id"], uniqueId(`S${nweChildId}`), setNodeState);
          break;
        case NODE_TYPES.sectionsummarygroup:
          handleNodeState(["id"], uniqueId(`${parentId}_SSG${nweChildId}`), setNodeState);
          break;
        case NODE_TYPES.questionGroup:
          handleNodeState(["id"], uniqueId(`${parentId}_QG${nweChildId}`), setNodeState);
          break;
        case NODE_TYPES.subquestiongroup:
          handleNodeState(["id"], uniqueId(`${parentId}_SQG${nweChildId}`), setNodeState);
          break;
        case NODE_TYPES.question:
          handleNodeState(["id"], uniqueId(`${parentId}_Q${nweChildId}`), setNodeState);
          break;
        default:
          handleNodeState(["id"], `N${nweChildId}`, setNodeState);
      }
    }
    // eslint-disable-next-line
  }, [nodeToAdd, INITIAL_FORM_STATE]);

  useEffect(() => {
    const prevNode = nodes?.find((n: any) => n?.id === nodeState?.id) || {};
    setIsEqual(_.isEqual(nodeState, prevNode));
  }, [nodeState, nodes]);
  useEffect(() => {
    if (nodeToEdit) {
      dispatch(setNodeToAdd(null));
      setNodeState(nodeToEdit);
    }
    // eslint-disable-next-line
  }, [nodeToEdit]);

  const handleClose = () => {
    setOpen(false);
    setIsDuplicateId(false);
    dispatch(setNodeToEdit(null));
    dispatch(setNodeToAdd(null));
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (nodes?.some((n: any) => n?.id === nodeState?.id && n?.id !== nodeToEdit?.id)) {
      setIsDuplicateId(true);
    } else if (nodeState?.id && /^[A-Za-z][A-Za-z0-9_]*$/.test(nodeState?.id) === true) {
      setIsDuplicateId(false);
      dispatch(
        nodeToEdit ? updateNode({ id: nodeToEdit?.id, newNodeData: nodeState }) : addNode(nodeState)
      );
      if (nodeToAdd) {
        const a = { ...nodeToAdd };
        setNodeState(INITIAL_FORM_STATE);
        dispatch(setNodeToAdd(a));
      }

      toast.success(`Node ${nodeToAdd ? "added" : "saved"} successfully!`);
      // handleClose();
    }
  };
  const handleAddCondition = (path: any, isGroup = false) => {
    setNodeState((prev: any) => {
      const newState = _.cloneDeep(prev);
      const currentValues = _.get(newState, path.join("."), []);

      let newCondition: any = { id: "", value: "", operator: "==" };
      if (isGroup) newCondition = [newCondition];

      _.set(newState, path.join("."), [
        ...(currentValues.length ? [...currentValues, "AND"] : []),
        newCondition,
      ]);

      return newState;
    });
  };
  const handleDeleteCondition = (path: any) => {
    setNodeState((prev: any) => {
      const newState = _.cloneDeep(prev);
      let parentPath = [...path];
      let index = parentPath.pop(); // Extract index to delete
      const values = _.get(newState, parentPath.join("."), []);

      let filteredValues = values.filter((_: any, i: any) => i !== index);

      // Remove orphaned logical operators
      if (filteredValues[index - 1] === "AND" || filteredValues[index - 1] === "OR") {
        filteredValues.splice(index - 1, 1);
      } else if (!index) {
        filteredValues.splice(index, 1);
      }

      _.set(
        newState,
        parentPath.join("."),
        typeof path[path.length - 2] === "string" && !filteredValues?.length ? true : filteredValues
      );

      return newState;
    });
  };
  const handleFieldChangeCondition = ({ target: { name, value } }: any, path: any) => {
    handleNodeState(name === "conditional_operator" ? path : [...path, name], value, setNodeState);
  };

  return (
    <>
      <FormControlLabel
        value="end"
        control={<Switch color="primary" />}
        label="Node Editor"
        sx={{ position: "fixed", right: 3, top: 3 }}
        labelPlacement="end"
      />
      <Drawer
        anchor="right"
        variant="permanent"
        sx={{ "& .MuiDrawer-paper": { width: "100%", maxWidth: "450px" } }}
        open={open}
        onClose={handleClose}
        PaperProps={{
          component: "form",
          onSubmit: handleSubmit,
        }}
      >
        {!nodeToAdd && !nodeToEdit ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              padding: 2,
              display: "grid",
              placeContent: "center",
            }}
          >
            <DrawTwoToneIcon sx={{ fontSize: 100, display: "block", mx: "auto", opacity: 0.5 }} />
            <Typography color="textDisabled"> Select a Node to Edit, or Add a New Node</Typography>
          </Box>
        ) : (
          <>
            <DialogTitle
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
            >
              {nodeToEdit ? "Edit" : "Add"} {title}
              <Box>
                <Button variant="contained" type="submit" disabled={isEqual}>
                  {nodeToEdit ? "Save" : "Add"}
                </Button>
              </Box>
            </DialogTitle>
            <DialogContent>
              <TextField
                required
                margin="dense"
                name="id"
                value={nodeState?.id ?? ""}
                label="ID"
                type="text"
                error={
                  isDuplicateId ||
                  (!!nodeState?.id && /^[A-Za-z][A-Za-z0-9_]*$/.test(nodeState?.id) === false)
                }
                fullWidth
                helperText={
                  nodeState?.id && /^[A-Za-z][A-Za-z0-9_]*$/.test(nodeState?.id) === false
                    ? "ID must start with a letter and can only contain letters, numbers, and underscores."
                    : isDuplicateId
                    ? "ID already exists. Please enter a unique ID."
                    : "ID can be entered only once and cannot be changed."
                }
                disabled={!!nodeToEdit}
                onChange={({ target: { name, value } }: any) => {
                  setIsDuplicateId(false);
                  handleNodeState([name], value, setNodeState);
                }}
              />
              <Divider sx={{ my: 1 }} />
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">Title</Typography>
                  <Switch
                    name="condition"
                    sx={{ display: "none" }}
                    checked={!!nodeState?.data?.properties?.title?.condition}
                    onChange={({ target: { name, checked } }: any) =>
                      handleNodeState(["data", "properties", "title", name], checked, setNodeState)
                    }
                  />
                </Box>

                {nodeState?.data?.properties?.title?.condition && (
                  <Box>
                    <Box>
                      <CreateConditions
                        condition={nodeState?.data?.properties?.title?.condition}
                        path={["data", "properties", "title", "condition"]} // Pass initial path
                        onDelete={handleDeleteCondition}
                        onAdd={handleAddCondition}
                        onFiledChange={handleFieldChangeCondition}
                      />
                    </Box>

                    <TextField
                      required
                      margin="dense"
                      name="ifValue"
                      label="If Value"
                      type="text"
                      value={nodeState?.data?.properties?.title?.ifValue ?? ""}
                      fullWidth
                      helperText="Type text between {{}} for links and [] for substitutions."
                      onChange={({ target: { name, value } }: any) => {
                        handleNodeState(["data", "nodeTitle"], value, setNodeState);
                        handleNodeState(["data", "properties", "title", name], value, setNodeState);
                      }}
                    />
                    {Array.isArray(nodeState?.data?.properties?.title?.condition) && (
                      <TextField
                        required
                        margin="dense"
                        name="elseValue"
                        label="Else Value"
                        type="text"
                        value={nodeState?.data?.properties?.title?.elseValue ?? ""}
                        fullWidth
                        helperText="Type text between {{}} for links and [] for substitutions."
                        onChange={({ target: { name, value } }: any) =>
                          handleNodeState(
                            ["data", "properties", "title", name],
                            value,
                            setNodeState
                          )
                        }
                      />
                    )}
                  </Box>
                )}
                <Box>
                  <Links {...{ nodeState, setNodeState }} />
                </Box>
                <Box>
                  <Substitutions {...{ nodeState, setNodeState, propKey: "title" }} />
                </Box>
              </Box>
              {isQuestionNode ? (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Box>
                    <Typography variant="subtitle1">Properties</Typography>
                    <TextField
                      margin="dense"
                      name="placeholder"
                      label="Placeholder (Optional)"
                      type="text"
                      value={nodeState?.data?.properties?.properties?.placeholder ?? ""}
                      fullWidth
                      onChange={({ target: { name, value } }: any) =>
                        handleNodeState(
                          ["data", "properties", "properties", name],
                          value,
                          setNodeState
                        )
                      }
                    />

                    <FormControl fullWidth margin="normal">
                      <InputLabel id="select_node_type">Type *</InputLabel>

                      <Select
                        required
                        labelId="select_node_type"
                        name="type"
                        value={nodeState?.data?.properties?.type ?? ""}
                        label="Type *"
                        onChange={({ target: { name, value } }: any) =>
                          handleNodeState(["data", "properties", name], value, setNodeState)
                        }
                      >
                        <MenuItem value="" sx={{ display: "none" }}></MenuItem>

                        {Object.keys(FIELD_TYPES)?.map((key: string) => (
                          <MenuItem key={key} value={FIELD_TYPES[key]}>
                            {FIELD_TYPES[key]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {fixedValueTypes.includes(nodeState?.data?.properties?.type) && (
                    <Values {...{ nodeState, setNodeState }} />
                  )}
                  <Box>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      Style
                    </Typography>
                    <FormControl fullWidth margin="dense">
                      <InputLabel id="select_direction">Direction (Optional)</InputLabel>
                      <Select
                        labelId="select_direction"
                        name="direction"
                        value={nodeState?.data?.style?.direction ?? ""}
                        label="Direction (Optional)"
                        onChange={({ target: { name, value } }: any) =>
                          handleNodeState(["data", "style", name], value, setNodeState)
                        }
                      >
                        <MenuItem value="" sx={{ display: "none" }}></MenuItem>
                        <MenuItem value="row">row</MenuItem>
                        <MenuItem value="column">
                          column
                          <Typography variant="caption" color="textDisabled" sx={{ ml: 1 }}>
                            (Default)
                          </Typography>
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      margin="dense"
                      type="text"
                      name="row"
                      label="Row (Optional)"
                      value={nodeState?.data?.style?.row ?? ""}
                      onChange={({ target: { name, value } }: any) =>
                        /^\d*$/.test(value) &&
                        handleNodeState(["data", "style", name], value, setNodeState)
                      }
                      fullWidth
                    />
                  </Box>

                  {!fixedValueTypes.includes(nodeState?.data?.properties?.type) && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Validations {...{ nodeState, setNodeState }} />
                    </>
                  )}
                  <Divider sx={{ my: 1 }} />
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="subtitle1">Default Value</Typography>
                      <Switch
                        name="condition"
                        checked={!!nodeState?.data?.properties?.defaultValue?.condition}
                        onChange={({ target: { name, checked } }: any) =>
                          handleNodeState(
                            ["data", "properties", "defaultValue", name],
                            checked,
                            setNodeState
                          )
                        }
                      />
                    </Box>

                    {nodeState?.data?.properties?.defaultValue?.condition && (
                      <Box>
                        <Box>
                          <CreateConditions
                            condition={nodeState?.data?.properties?.defaultValue?.condition}
                            path={["data", "properties", "defaultValue", "condition"]} // Pass initial path
                            onDelete={handleDeleteCondition}
                            onAdd={handleAddCondition}
                            onFiledChange={handleFieldChangeCondition}
                          />
                        </Box>
                        <TextField
                          margin="dense"
                          name="ifValue"
                          label="If Value (Optional)"
                          type="text"
                          value={nodeState?.data?.properties?.defaultValue?.ifValue ?? ""}
                          fullWidth
                          helperText="Type text between [] for substitutions."
                          onChange={({ target: { name, value } }: any) =>
                            handleNodeState(
                              ["data", "properties", "defaultValue", name],
                              value,
                              setNodeState
                            )
                          }
                        />
                        {Array.isArray(nodeState?.data?.properties?.defaultValue?.condition) && (
                          <TextField
                            margin="dense"
                            name="elseValue"
                            label="Else Value (Optional)"
                            type="text"
                            value={nodeState?.data?.properties?.defaultValue?.elseValue ?? ""}
                            fullWidth
                            helperText="Type text between [] for substitutions."
                            onChange={({ target: { name, value } }: any) =>
                              handleNodeState(
                                ["data", "properties", "defaultValue", name],
                                value,
                                setNodeState
                              )
                            }
                          />
                        )}
                        <Box>
                          <Substitutions
                            {...{ nodeState, setNodeState, propKey: "defaultValue" }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="subtitle1">Required</Typography>
                      <Switch
                        name="condition"
                        checked={!!nodeState?.data?.required?.condition}
                        onChange={({ target: { name, checked } }: any) =>
                          handleNodeState(["data", "required", name], checked, setNodeState)
                        }
                      />
                    </Box>
                    {nodeState?.data?.required?.condition && (
                      <Box>
                        <CreateConditions
                          condition={nodeState?.data?.required?.condition}
                          path={["data", "required", "condition"]} // Pass initial path
                          onDelete={handleDeleteCondition}
                          onAdd={handleAddCondition}
                          onFiledChange={handleFieldChangeCondition}
                        />
                      </Box>
                    )}
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="subtitle1">Editable</Typography>
                      <Switch
                        name="condition"
                        checked={!!nodeState?.data?.properties?.editable?.condition}
                        onChange={({ target: { name, checked } }: any) =>
                          handleNodeState(
                            ["data", "properties", "editable", name],
                            checked,
                            setNodeState
                          )
                        }
                      />
                    </Box>
                    {nodeState?.data?.properties?.editable?.condition && (
                      <Box>
                        <CreateConditions
                          condition={nodeState?.data?.properties?.editable?.condition}
                          path={["data", "properties", "editable", "condition"]} // Pass initial path
                          onDelete={handleDeleteCondition}
                          onAdd={handleAddCondition}
                          onFiledChange={handleFieldChangeCondition}
                        />
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  {!isSectionSummeryGroupNode && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ mt: 1 }}>
                          Style
                        </Typography>
                        {isSectionNode && (
                          <FormControl fullWidth margin="dense">
                            <InputLabel id="select_icon">Icon (Optional)</InputLabel>
                            <Select
                              labelId="select_icon"
                              name="icon"
                              value={nodeState?.data?.icon ?? ""}
                              label="Icon (Optional)"
                              onChange={({ target: { name, value } }: any) =>
                                handleNodeState(["data", name], value, setNodeState)
                              }
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>

                              {Object.keys(ICON_OPTIONS)?.map((key: string) => (
                                <MenuItem key={key} value={ICON_OPTIONS[key]}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box
                                      sx={{
                                        width: "24px",
                                        display: "grid",
                                        placeContent: "center",
                                      }}
                                    >
                                      <DisplayIcon icon={ICON_OPTIONS[key]} />
                                    </Box>
                                    {ICON_OPTIONS[key]}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                        {isQuestionGroupNode && (
                          <>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography variant="subtitle2">Box Layout</Typography>
                              <Switch
                                name="boxLayout"
                                checked={!!nodeState?.data?.boxLayout}
                                onChange={({ target: { name, checked } }: any) => {
                                  handleNodeState(
                                    ["data", name],
                                    checked ? "TRUE" : "",
                                    setNodeState
                                  );
                                  !checked &&
                                    handleNodeState(["data", "boxIcon"], "", setNodeState);
                                  checked &&
                                    handleNodeState(
                                      ["data", "style", "justify"],
                                      "center",
                                      setNodeState
                                    );
                                }}
                              />
                            </Box>
                            {nodeState?.data?.boxLayout && (
                              <FormControl fullWidth margin="dense">
                                <InputLabel id="select_boxIcon">Box Icon (Optional)</InputLabel>
                                <Select
                                  labelId="select_boxIcon"
                                  name="boxIcon"
                                  value={nodeState?.data?.boxIcon ?? ""}
                                  label="Box Icon (Optional)"
                                  onChange={({ target: { name, value } }: any) =>
                                    handleNodeState(["data", name], value, setNodeState)
                                  }
                                >
                                  <MenuItem value="">
                                    <em>None</em>
                                  </MenuItem>

                                  {Object.keys(BOXICON_OPTIONS)?.map((key: string) => (
                                    <MenuItem key={key} value={BOXICON_OPTIONS[key]}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Box
                                          sx={{
                                            width: "24px",
                                            display: "grid",
                                            placeContent: "center",
                                          }}
                                        >
                                          <DisplayIcon icon={BOXICON_OPTIONS[key]} />
                                        </Box>
                                        {BOXICON_OPTIONS[key]}
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </>
                        )}
                        {!isSectionNode && (
                          <FormControl fullWidth margin="dense">
                            <InputLabel id="select_justify">Justify (Optional)</InputLabel>
                            <Select
                              labelId="select_justify"
                              name="justify"
                              value={nodeState?.data?.style?.justify ?? ""}
                              label="Justify (Optional)"
                              onChange={({ target: { name, value } }: any) =>
                                handleNodeState(["data", "style", name], value, setNodeState)
                              }
                              disabled={nodeState?.data?.boxLayout === "TRUE"}
                            >
                              <MenuItem value="" sx={{ display: "none" }}></MenuItem>
                              <MenuItem value="start">Start</MenuItem>
                              <MenuItem value="center">
                                Center
                                <Typography variant="caption" color="textDisabled" sx={{ ml: 1 }}>
                                  (Default)
                                </Typography>
                              </MenuItem>
                              <MenuItem value="end">End</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    </>
                  )}
                </>
              )}
              <Divider sx={{ my: 1 }} />
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="subtitle1">Visibility</Typography>
                  <Switch
                    name="condition"
                    checked={!!nodeState?.data?.visibility?.condition}
                    onChange={({ target: { name, checked } }: any) =>
                      handleNodeState(["data", "visibility", name], checked, setNodeState)
                    }
                  />
                </Box>
                {nodeState?.data?.visibility?.condition && (
                  <Box>
                    <CreateConditions
                      condition={nodeState?.data?.visibility?.condition}
                      path={["data", "visibility", "condition"]} // Pass initial path
                      onDelete={handleDeleteCondition}
                      onAdd={handleAddCondition}
                      onFiledChange={handleFieldChangeCondition}
                    />
                  </Box>
                )}
              </Box>
              {nodeToEdit && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Button
                    onClick={() => setIsDeletePopupOpen(true)}
                    color="error"
                    variant="outlined"
                  >
                    <Delete sx={{ mr: 1 }} />
                    Delete Node
                  </Button>
                </>
              )}
            </DialogContent>
          </>
        )}
      </Drawer>
      <ConfirmActionPopup
        {...{
          open: isDeletePopupOpen,
          onClose: () => setIsDeletePopupOpen(false),
          onConfirm: () => dispatch(deleteNode(nodeToEdit?.id)) && setIsDeletePopupOpen(false),
          text: "delete",
        }}
      />
    </>
  );
}
